import { useState, useMemo, useRef, useEffect } from "react";
import { TextField, Chip, Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useTranslation } from "react-i18next";

/**
 * @interface MultiSelectOptionDetail
 * Represents the details of a multi-select option.
 */
interface MultiSelectOptionDetail {
  id: string;
  name: string;
}

/**
 * @interface CustomMultiSelectAutocompleteProps
 * Props for the CustomMultiSelectAutocomplete component.
 */
interface CustomMultiSelectAutocompleteProps {
  options: MultiSelectOptionDetail[];
  value: MultiSelectOptionDetail[];
  // eslint-disable-next-line no-unused-vars
  onChange: (newValue: MultiSelectOptionDetail[]) => void;
}

/**
 * @function useStyles
 * Custom styles for the component.
 */
const useStyles = makeStyles((theme) => ({
  chip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.1),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    "& .MuiChip-deleteIcon": {
      color: theme.palette.common.white,
    },
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    height: 1,
    backgroundColor: theme.palette.divider,
  },
}));

/**
 * Custom multi-select autocomplete component.
 * Renders a list of selectable options with selected values and provides a callback for when selections change.
 * Supports shift+click to select a range of visible options,
 * and includes a "Select all matching" shortcut with a divider.
 *
 * @property {MultiSelectOptionDetail[]} options - Array of selectable options.
 * @property {MultiSelectOptionDetail[]} value - Currently selected options.
 * @property {(newValue: MultiSelectOptionDetail[]) => void} props.onChange - Callback when selected options change.
 * @returns {JSX.Element} The CustomMultiSelectAutocomplete component.
 */
const CustomMultiSelectAutocomplete = ({
  options,
  value,
  onChange,
}: CustomMultiSelectAutocompleteProps) => {
  const classes = useStyles();
  const { t } = useTranslation(["common"]);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastClickedIndexRef = useRef<number | null>(null);
  const justSelectedRef = useRef(false);
  const lastInputValueRef = useRef<string>("");

  /**
   * @constant SELECT_ALL_OPTION
   * Represents the "Select all matching" option.
   */
  const SELECT_ALL_OPTION: MultiSelectOptionDetail = {
    id: "__select_all__",
    name: t("forms.multiSelectAll", { ns: "common" }),
  };

  /**
   * @constant filteredOptions
   * Filters the options based on the input value.
   */
  const filteredOptions = useMemo(() => {
    const lower = inputValue.toLowerCase();
    return options.filter((opt) => opt.name.toLowerCase().includes(lower));
  }, [inputValue, options]);

  /**
   * @constant displayOptions
   * Displays the filtered options, including the "Select all matching" option if applicable.
   */
  const displayOptions = useMemo(() => {
    return filteredOptions.length > 0 ? [SELECT_ALL_OPTION, ...filteredOptions] : [];
  }, [filteredOptions]);

  /**
   * Effect to handle the opening of the autocomplete.
   * Sets the input value to the last input value when just selected.
   */
  useEffect(() => {
    if (justSelectedRef.current) {
      setOpen(true);
      // restore inputValue to keep filtering
      setInputValue(lastInputValueRef.current);
      justSelectedRef.current = false;
    }
  }, [value]);

  /**
   * Handles the change event of the autocomplete.
   * Updates the selected values based on user interaction.
   *
   * @param {unknown} event - The event object.
   * @param {MultiSelectOptionDetail[]} newValue - The new selected values.
   */
  const handleChange = (event: unknown, newValue: MultiSelectOptionDetail[]) => {
    const rawEvent = event as unknown as MouseEvent;
    const clickedItem = newValue[newValue.length - 1];

    justSelectedRef.current = true;
    lastInputValueRef.current = inputValue;

    if (!clickedItem) {
      onChange(newValue);
      return;
    }

    if (clickedItem.id === SELECT_ALL_OPTION.id) {
      const allNew = filteredOptions.filter(
        (item) => !value.some((v) => v.id === item.id)
      );
      onChange([...value, ...allNew]);
      lastInputValueRef.current = "";
      return;
    }

    const clickedIndex = filteredOptions.findIndex((o) => o.id === clickedItem.id);

    if (rawEvent.shiftKey && clickedIndex !== -1 && rawEvent.type === "click") {
      const start = lastClickedIndexRef.current ?? clickedIndex;
      const [from, to] = [Math.min(start, clickedIndex), Math.max(start, clickedIndex)];
      const rangeItems = filteredOptions.slice(from, to + 1);
      const updated = [
        ...value,
        ...rangeItems.filter((item) => !value.some((v) => v.id === item.id)),
      ];
      onChange(updated);
      return;
    }

    onChange(newValue);

    if (clickedIndex !== -1) {
      lastClickedIndexRef.current = clickedIndex;
      lastInputValueRef.current = "";
    }
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        if (!justSelectedRef.current) {
          setOpen(false);
        }
      }}
      options={displayOptions}
      value={value}
      inputValue={inputValue}
      onInputChange={(e, val, reason) => {
        setInputValue(val);
        lastInputValueRef.current = val;
        if (reason === "input") setOpen(true);
      }}
      getOptionLabel={(option) => option.name}
      getOptionSelected={(option, selectedValue) => option.id === selectedValue.id}
      filterOptions={(allOptions, state) =>
        allOptions.filter(
          (opt) =>
            opt.id === SELECT_ALL_OPTION.id ||
            opt.name.toLowerCase().includes(state.inputValue.toLowerCase())
        )
      }
      onChange={handleChange}
      renderOption={(option) => (
        <div
          style={{ width: "100%" }}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          <div>{option.name}</div>
          {option.id === SELECT_ALL_OPTION.id && <Divider className={classes.divider} />}
        </div>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            className={classes.chip}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          inputRef={(ref) => {
            inputRef.current = ref;
            if (
              "inputRef" in params &&
              params.inputRef &&
              typeof params.inputRef === "object"
            ) {
              (
                params.inputRef as React.MutableRefObject<HTMLInputElement | null>
              ).current = ref;
            }
          }}
        />
      )}
    />
  );
};

export default CustomMultiSelectAutocomplete;
